import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  BarChart3, 
  TrendingUp, 
  Users,
  Building2,
  DollarSign,
  Calendar,
  Loader2,
  RefreshCw,
  Sun,
  Moon,
  LogOut,
  ArrowUpRight,
  ArrowDownRight,
  Coins,
  FileText,
  Receipt,
  CreditCard,
  Wallet,
  IndianRupee,
  Download,
  Calculator,
  Briefcase,
  ShieldCheck,
  History,
  CalendarRange,
  FileDown,
  Search,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { ProfileModal } from '../components/ProfileModal';
import ReportsTab from '../components/reports/ReportsTab';
import RoleAnalytics from '../components/analytics/RoleAnalytics';
import { ContractsTab } from '../components/contracts/ContractsTab';

const LOGO_URL = '/logo.webp';

const PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week', label: 'This Week' },
  { id: 'last_7', label: 'Last 7 days' },
  { id: 'month', label: 'This Month' },
  { id: 'last_30', label: 'Last 30 days' },
  { id: 'quarter', label: 'This Quarter' },
  { id: 'year', label: 'This Year' },
  { id: 'all', label: 'All Time' },
];

const computePreset = (id) => {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const end = new Date(today);
  switch (id) {
    case 'today': return { start: iso(today), end: iso(today) };
    case 'yesterday': {
      const y = new Date(today); y.setDate(y.getDate() - 1);
      return { start: iso(y), end: iso(y) };
    }
    case 'week': {
      const s = new Date(today); s.setDate(today.getDate() - today.getDay());
      return { start: iso(s), end: iso(end) };
    }
    case 'last_7': { const s = new Date(today); s.setDate(s.getDate() - 6); return { start: iso(s), end: iso(end) }; }
    case 'month': return { start: iso(new Date(today.getFullYear(), today.getMonth(), 1)), end: iso(end) };
    case 'last_30': { const s = new Date(today); s.setDate(s.getDate() - 29); return { start: iso(s), end: iso(end) }; }
    case 'quarter': {
      const q = Math.floor(today.getMonth() / 3);
      return { start: iso(new Date(today.getFullYear(), q * 3, 1)), end: iso(end) };
    }
    case 'year': return { start: iso(new Date(today.getFullYear(), 0, 1)), end: iso(end) };
    default: return { start: '', end: '' };
  }
};

const fmtCurrency = (amount) => {
  if (amount == null) return '₹0';
  if (Math.abs(amount) >= 10000000) return `₹${(amount / 10000000).toFixed(2)}Cr`;
  if (Math.abs(amount) >= 100000) return `₹${(amount / 100000).toFixed(2)}L`;
  if (Math.abs(amount) >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
  return `₹${amount?.toLocaleString('en-IN', { maximumFractionDigits: 2 }) || 0}`;
};

const toCSV = (rows, columns) => {
  if (!rows?.length) return '';
  const head = columns.join(',');
  const body = rows.map((r) =>
    columns.map((c) => {
      const v = r[c];
      if (v == null) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    }).join(',')
  ).join('\n');
  return `${head}\n${body}`;
};

const downloadCSV = (rows, columns, filename) => {
  const csv = toCSV(rows, columns);
  if (!csv) return;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
};

const FinanceDashboard = () => {
  const navigate = useNavigate();
  const { user, api, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [loading, setLoading] = useState({});
  const [revenueReport, setRevenueReport] = useState(null);
  const [gstReport, setGstReport] = useState(null);
  const [creditAudit, setCreditAudit] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [activeTab, setActiveTab] = useState('reports');
  const [preset, setPreset] = useState('month');
  const [{ start: startDate, end: endDate }, setRange] = useState(computePreset('month'));
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const EMPTY_INVOICE_FILTERS = { paidFrom: '', paidTo: '', centre: 'all', paymentType: 'all', status: 'all', gst: 'all' };
  const [invoiceFilters, setInvoiceFilters] = useState(EMPTY_INVOICE_FILTERS);
  const [txnTypeFilter, setTxnTypeFilter] = useState('all');

  /* eslint-disable */
  // Each section loads independently so one slow endpoint never blocks the
  // whole page; a sequence number per section drops stale responses when the
  // date range changes mid-flight.
  const requestSeq = useRef({});
  const loadSection = async (key, url, setter) => {
    const seq = (requestSeq.current[key] || 0) + 1;
    requestSeq.current[key] = seq;
    setLoading((s) => ({ ...s, [key]: true }));
    try {
      const res = await api.get(url);
      if (requestSeq.current[key] === seq) setter(res.data);
    } catch (error) {
      if (requestSeq.current[key] !== seq) return;
      console.error(`Error fetching ${key}:`, error);
      toast.error('Failed to load finance data', { id: 'finance-load-error' });
    } finally {
      if (requestSeq.current[key] === seq) setLoading((s) => ({ ...s, [key]: false }));
    }
  };

  const dateParams = startDate && endDate ? `?start_date=${startDate}&end_date=${endDate}` : '?period=all';

  const fetchTransactions = () => {
    const txnExtra = txnTypeFilter && txnTypeFilter !== 'all' ? `&transaction_type=${txnTypeFilter}` : '';
    loadSection('transactions', `/finance/transactions${dateParams}${txnExtra}`, setTransactions);
  };

  const fetchReports = () => {
    loadSection('revenue', `/finance/revenue-report${dateParams}`, setRevenueReport);
    loadSection('gst', `/finance/gst-report${dateParams}`, setGstReport);
    loadSection('credits', `/finance/credit-audit${dateParams}`, setCreditAudit);
    loadSection('invoices', `/finance/invoices${dateParams}`, setInvoices);
  };

  const fetchData = () => {
    fetchReports();
    fetchTransactions();
  };

  useEffect(() => {
    fetchReports();
  }, [api, startDate, endDate]);

  // The type filter only affects the transactions list — don't refetch everything.
  useEffect(() => {
    fetchTransactions();
  }, [api, startDate, endDate, txnTypeFilter]);

  const applyPreset = (id) => {
    setPreset(id);
    setRange(computePreset(id));
  };

  const onStartDateChange = (v) => { setRange((r) => ({ ...r, start: v })); setPreset('custom'); };
  const onEndDateChange = (v) => { setRange((r) => ({ ...r, end: v })); setPreset('custom'); };
  /* eslint-enable */

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toLocaleString() || 0}`;
  };

  const exportToCSV = (data, filename) => {
    // Simple CSV export functionality
    toast.info('Export functionality - contact admin for detailed reports');
  };

  const sidebarItems = [
    { id: 'analytics', label: 'Analytics', icon: TrendingUp },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'revenue', label: 'Revenue Report', icon: IndianRupee },
    { id: 'gst', label: 'GST Report', icon: Calculator },
    { id: 'credits', label: 'Credit Audit', icon: Coins },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'transactions', label: 'Transactions', icon: History },
    { id: 'contracts', label: 'Contracts', icon: FileText },
  ];

  // Theme-aware colors
  const sidebarBg = isDark ? 'bg-[#0a0b0d]' : 'bg-[#faf8f3]';
  const sidebarBorder = isDark ? 'border-[#1e2128]' : 'border-[#e5dfd0]';
  const cardBg = isDark ? 'bg-[#13161a]' : 'bg-white';
  const cardBorder = isDark ? 'border-[#1e2128]' : 'border-[#e5dfd0]';
  const innerCardBg = isDark ? 'bg-[#0a0b0d]' : 'bg-[#faf8f3]';
  const hoverBg = isDark ? 'hover:bg-[#13161a]' : 'hover:bg-[#efe9da]';

  // Tabs backed by the /finance/* endpoints (ids match loadSection keys)
  const tabData = { revenue: revenueReport, gst: gstReport, credits: creditAudit, invoices, transactions };
  const anyLoading = Object.values(loading).some(Boolean);

  // Invoices: client-side filters on top of the global date range.
  // Payment dates are compared in the viewer's local timezone (IST), not UTC.
  const toLocalDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const allInvoices = invoices?.invoices || [];
  const invoiceCentres = [...new Set(allInvoices.map((i) => i.venue_name).filter(Boolean))].sort();
  const invoicePaymentTypes = [...new Set(allInvoices.map((i) => i.payment_method).filter(Boolean))].sort();
  const invoiceFiltersActive = invoiceSearch.trim() !== '' ||
    Object.keys(EMPTY_INVOICE_FILTERS).some((k) => invoiceFilters[k] !== EMPTY_INVOICE_FILTERS[k]);
  const filteredInvoices = allInvoices.filter((inv) => {
    const q = invoiceSearch.trim().toLowerCase();
    if (q && !`${inv.invoice_number || ''} ${inv.customer_name || ''} ${inv.customer_email || ''} ${inv.gstin || ''} ${inv.booking_id || ''} ${inv.description || ''}`.toLowerCase().includes(q)) return false;
    if (invoiceFilters.centre !== 'all' && inv.venue_name !== invoiceFilters.centre) return false;
    if (invoiceFilters.paymentType !== 'all' && inv.payment_method !== invoiceFilters.paymentType) return false;
    if (invoiceFilters.status !== 'all' && inv.status !== invoiceFilters.status) return false;
    if (invoiceFilters.gst === 'with' && !inv.gstin) return false;
    if (invoiceFilters.gst === 'without' && inv.gstin) return false;
    if (invoiceFilters.paidFrom || invoiceFilters.paidTo) {
      const paid = toLocalDate(inv.paid_at);
      if (!paid) return false;
      if (invoiceFilters.paidFrom && paid < invoiceFilters.paidFrom) return false;
      if (invoiceFilters.paidTo && paid > invoiceFilters.paidTo) return false;
    }
    return true;
  });
  const sumTotal = (rows) => rows.reduce((s, i) => s + (i.total_amount || 0), 0);
  const filteredPaid = filteredInvoices.filter((i) => i.status === 'paid');
  const filteredPending = filteredInvoices.filter((i) => i.status !== 'paid');
  const setInvoiceFilter = (key, value) => setInvoiceFilters((f) => ({ ...f, [key]: value }));
  const showTabSpinner = activeTab in tabData && loading[activeTab] && !tabData[activeTab];

  return (
    <div className="min-h-screen bg-background" data-testid="finance-dashboard">
      <div className="flex">
        {/* Sidebar */}
        <aside className={`hidden lg:flex w-64 flex-col fixed inset-y-0 z-50 ${sidebarBg} border-r ${sidebarBorder}`}>
          <div className={`p-6 border-b ${sidebarBorder}`}>
            <Link to="/finance" className="flex flex-col gap-1.5">
              <img src={LOGO_URL} alt="Clayworks" className="h-7 w-auto" />
              <p className="text-xs font-medium text-muted-foreground tracking-wide">Finance & Accounts</p>
            </Link>
          </div>
          
          <nav className="flex-1 p-4 space-y-1">
            {sidebarItems.map(item => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  activeTab === item.id 
                    ? 'bg-primary text-primary-foreground' 
                    : `text-muted-foreground hover:text-foreground ${hoverBg}`
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            ))}
          </nav>

          <div className={`p-4 border-t ${sidebarBorder}`}>
            <button
              onClick={toggleTheme}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground ${hoverBg} mb-2`}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              {isDark ? 'Light Mode' : 'Dark Mode'}
            </button>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowProfileModal(true)}
                className={`flex items-center gap-2 flex-1 min-w-0 p-2 rounded-xl ${cardBg} hover:ring-1 hover:ring-primary/30 transition-all cursor-pointer text-left`}
                data-testid="sidebar-profile-btn"
              >
                <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                  <Users className="w-3.5 h-3.5 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium truncate">{user?.name}</p>
                  <p className="text-[10px] text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
                </div>
              </button>
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-destructive shrink-0 px-2"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </aside>

        <ProfileModal
          open={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          user={user}
          api={api}
          onProfileUpdate={() => window.location.reload()}
        />

        {/* Main Content */}
        <main className="flex-1 lg:ml-64 min-w-0 overflow-x-hidden">
          <div className="p-6 lg:p-8 min-w-0">
            {/* Header */}
            <div className="flex flex-col gap-4 mb-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h1 className="font-heading text-2xl sm:text-3xl font-bold">
                    {sidebarItems.find(i => i.id === activeTab)?.label || 'Finance Dashboard'}
                  </h1>
                  <p className="text-muted-foreground mt-1">
                    Financial reports & audit trails {startDate && endDate && (
                      <span className="ml-2 text-xs">· {startDate} → {endDate}</span>
                    )}
                  </p>
                </div>
                <div className="flex gap-2 items-center">
                  <Button variant="outline" size="sm" className={`${cardBorder} ${hoverBg}`} onClick={fetchData}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${anyLoading ? 'animate-spin' : ''}`} /> Refresh
                  </Button>
                  <Badge variant="outline" className="text-xs py-2">
                    <ShieldCheck className="w-3 h-3 mr-1" /> Read Only
                  </Badge>
                </div>
              </div>

              {/* Date range — global (hidden on Reports + Analytics, which each have their own pickers) */}
              {activeTab !== 'reports' && activeTab !== 'analytics' && (
                <Card className={`${cardBg} ${cardBorder}`}>
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                      <div className="flex items-center gap-2 text-sm font-medium shrink-0">
                        <CalendarRange className="w-4 h-4 text-primary" />
                        Date Range:
                      </div>
                      <div className="flex flex-wrap gap-1.5 flex-1">
                        {PRESETS.map((p) => (
                          <button
                            key={p.id}
                            onClick={() => applyPreset(p.id)}
                            data-testid={`finance-preset-${p.id}`}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                              preset === p.id
                                ? 'bg-primary text-primary-foreground border-primary'
                                : `${cardBorder} hover:ring-1 hover:ring-primary/30 ${hoverBg}`
                            }`}
                          >
                            {p.label}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="date"
                          value={startDate}
                          onChange={(e) => onStartDateChange(e.target.value)}
                          className="h-9 w-36"
                          data-testid="finance-from-date"
                        />
                        <span className="text-muted-foreground text-xs">to</span>
                        <Input
                          type="date"
                          value={endDate}
                          onChange={(e) => onEndDateChange(e.target.value)}
                          className="h-9 w-36"
                          data-testid="finance-to-date"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Analytics Tab — role-scoped dashboard (Finance vs Audits) */}
            {activeTab === 'analytics' && (
              <RoleAnalytics role={user?.role === 'finance_audit' ? 'audits' : 'finance'} />
            )}

            {/* Contracts Tab — read-only for finance / finance_audit */}
            {activeTab === 'contracts' && (
              <ContractsTab api={api} venues={[]} userRole={user?.role} />
            )}

            {/* Reports Tab — 7 audit reports with CSV/Excel export */}
            {activeTab === 'reports' && (
              <ReportsTab
                api={api}
                cardBg={cardBg}
                cardBorder={cardBorder}
                innerCardBg={isDark ? 'bg-[#0a0b0d]' : 'bg-[#faf8f3]'}
                inputBg={isDark ? 'bg-[#0a0b0d] border-[#1e2128]' : 'bg-[#faf8f3] border-[#e5dfd0]'}
              />
            )}

            {showTabSpinner && (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            )}

            {/* Revenue Report Tab */}
            {activeTab === 'revenue' && revenueReport && (
              <div className="space-y-8">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border-emerald-500/30">
                    <CardContent className="p-6">
                      <p className="text-sm text-emerald-600 dark:text-emerald-400">Gross Revenue</p>
                      <p className="text-3xl font-heading font-bold mt-2">{formatCurrency(revenueReport.summary.gross_revenue)}</p>
                      <p className="text-xs text-muted-foreground mt-2">{revenueReport.summary.total_bookings} bookings</p>
                    </CardContent>
                  </Card>

                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardContent className="p-6">
                      <p className="text-sm text-muted-foreground">Base Revenue</p>
                      <p className="text-3xl font-heading font-bold mt-2">{formatCurrency(revenueReport.summary.base_revenue)}</p>
                      <p className="text-xs text-muted-foreground mt-2">Before GST</p>
                    </CardContent>
                  </Card>

                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardContent className="p-6">
                      <p className="text-sm text-muted-foreground">GST Collected</p>
                      <p className="text-3xl font-heading font-bold mt-2">{formatCurrency(revenueReport.summary.gst_collected)}</p>
                      <p className="text-xs text-muted-foreground mt-2">Tax liability</p>
                    </CardContent>
                  </Card>

                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardContent className="p-6">
                      <p className="text-sm text-muted-foreground">Credits Used</p>
                      <p className="text-3xl font-heading font-bold mt-2">{revenueReport.summary.credits_used?.toFixed(1) || 0}</p>
                      <p className="text-xs text-muted-foreground mt-2">{revenueReport.summary.credit_bookings} bookings</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Venue Breakdown */}
                <Card className={`${cardBg} ${cardBorder}`}>
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-primary" />
                      Revenue by Venue
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className={`border-b ${cardBorder}`}>
                            <th className="text-left p-3 font-medium text-muted-foreground">Venue</th>
                            <th className="text-right p-3 font-medium text-muted-foreground">Revenue</th>
                            <th className="text-right p-3 font-medium text-muted-foreground">GST</th>
                            <th className="text-right p-3 font-medium text-muted-foreground">Bookings</th>
                          </tr>
                        </thead>
                        <tbody>
                          {revenueReport.venue_breakdown?.slice(0, 15).map((venue, idx) => (
                            <tr key={idx} className={`border-b ${cardBorder} ${hoverBg}`}>
                              <td className="p-3 font-medium">{venue.venue}</td>
                              <td className="p-3 text-right">{formatCurrency(venue.revenue)}</td>
                              <td className="p-3 text-right text-muted-foreground">{formatCurrency(venue.gst)}</td>
                              <td className="p-3 text-right">{venue.bookings}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {(!revenueReport.venue_breakdown || revenueReport.venue_breakdown.length === 0) && (
                        <p className="text-center text-muted-foreground py-8">No venue data for this period</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Daily Breakdown */}
                <Card className={`${cardBg} ${cardBorder}`}>
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-primary" />
                      Daily Revenue Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {revenueReport.daily_breakdown?.map((day, idx) => (
                        <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${innerCardBg}`}>
                          <span className="font-mono text-sm">{day.date}</span>
                          <div className="flex items-center gap-6">
                            <span className="text-sm text-muted-foreground">{day.bookings} bookings</span>
                            <span className="font-heading font-semibold">{formatCurrency(day.revenue)}</span>
                          </div>
                        </div>
                      ))}
                      {(!revenueReport.daily_breakdown || revenueReport.daily_breakdown.length === 0) && (
                        <p className="text-center text-muted-foreground py-8">No daily data for this period</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* GST Report Tab */}
            {activeTab === 'gst' && gstReport && (
              <div className="space-y-8">
                {/* GST Summary */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-blue-500/20 to-blue-500/5 border-blue-500/30">
                    <CardContent className="p-6">
                      <p className="text-sm text-blue-600 dark:text-blue-400">Total GST Collected</p>
                      <p className="text-3xl font-heading font-bold mt-2">{formatCurrency(gstReport.total.gst_collected)}</p>
                    </CardContent>
                  </Card>

                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardContent className="p-6">
                      <p className="text-sm text-muted-foreground">Taxable Value</p>
                      <p className="text-3xl font-heading font-bold mt-2">{formatCurrency(gstReport.total.taxable_value)}</p>
                    </CardContent>
                  </Card>

                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardContent className="p-6">
                      <p className="text-sm text-muted-foreground">CGST</p>
                      <p className="text-3xl font-heading font-bold mt-2">{formatCurrency(gstReport.total.cgst)}</p>
                    </CardContent>
                  </Card>

                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardContent className="p-6">
                      <p className="text-sm text-muted-foreground">SGST</p>
                      <p className="text-3xl font-heading font-bold mt-2">{formatCurrency(gstReport.total.sgst)}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* B2B vs B2C */}
                <div className="grid lg:grid-cols-2 gap-6">
                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardHeader>
                      <CardTitle className="font-heading flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-primary" />
                        B2B Transactions
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className={`p-4 rounded-xl ${innerCardBg}`}>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Taxable Value</span>
                            <span className="font-heading font-semibold">{formatCurrency(gstReport.b2b.taxable)}</span>
                          </div>
                        </div>
                        <div className={`p-4 rounded-xl ${innerCardBg}`}>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">GST Collected</span>
                            <span className="font-heading font-semibold">{formatCurrency(gstReport.b2b.gst)}</span>
                          </div>
                        </div>
                        <div className={`p-4 rounded-xl ${innerCardBg}`}>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Invoice Count</span>
                            <span className="font-heading font-semibold">{gstReport.b2b.invoices}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardHeader>
                      <CardTitle className="font-heading flex items-center gap-2">
                        <Users className="w-5 h-5 text-primary" />
                        B2C Transactions
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className={`p-4 rounded-xl ${innerCardBg}`}>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Taxable Value</span>
                            <span className="font-heading font-semibold">{formatCurrency(gstReport.b2c.taxable)}</span>
                          </div>
                        </div>
                        <div className={`p-4 rounded-xl ${innerCardBg}`}>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">GST Collected</span>
                            <span className="font-heading font-semibold">{formatCurrency(gstReport.b2c.gst)}</span>
                          </div>
                        </div>
                        <div className={`p-4 rounded-xl ${innerCardBg}`}>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Invoice Count</span>
                            <span className="font-heading font-semibold">{gstReport.b2c.invoices}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* GST by Rate */}
                <Card className={`${cardBg} ${cardBorder}`}>
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Calculator className="w-5 h-5 text-primary" />
                      GST by Tax Rate
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className={`border-b ${cardBorder}`}>
                            <th className="text-left p-3 font-medium text-muted-foreground">GST Rate</th>
                            <th className="text-right p-3 font-medium text-muted-foreground">Taxable Value</th>
                            <th className="text-right p-3 font-medium text-muted-foreground">GST Amount</th>
                            <th className="text-right p-3 font-medium text-muted-foreground">Invoices</th>
                          </tr>
                        </thead>
                        <tbody>
                          {gstReport.by_rate?.map((rate, idx) => (
                            <tr key={idx} className={`border-b ${cardBorder} ${hoverBg}`}>
                              <td className="p-3 font-medium">{rate.gst_rate}%</td>
                              <td className="p-3 text-right">{formatCurrency(rate.taxable_value)}</td>
                              <td className="p-3 text-right">{formatCurrency(rate.gst_amount)}</td>
                              <td className="p-3 text-right">{rate.invoices}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {(!gstReport.by_rate || gstReport.by_rate.length === 0) && (
                        <p className="text-center text-muted-foreground py-8">No GST data for this period</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Credit Audit Tab */}
            {activeTab === 'credits' && creditAudit && (
              <div className="space-y-8">
                {/* Credit Summary */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-amber-500/20 to-amber-500/5 border-amber-500/30">
                    <CardContent className="p-6">
                      <p className="text-sm text-amber-600 dark:text-amber-400">Total Credits in Wallets</p>
                      <p className="text-3xl font-heading font-bold mt-2">{creditAudit.summary.total_credits_in_wallets?.toFixed(1) || 0}</p>
                    </CardContent>
                  </Card>

                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardContent className="p-6">
                      <p className="text-sm text-muted-foreground">Monthly Allocation</p>
                      <p className="text-3xl font-heading font-bold mt-2">{creditAudit.summary.total_monthly_allocation?.toFixed(1) || 0}</p>
                    </CardContent>
                  </Card>

                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardContent className="p-6">
                      <p className="text-sm text-muted-foreground">Used in Period</p>
                      <p className="text-3xl font-heading font-bold mt-2">{creditAudit.summary.credits_used_in_period?.toFixed(1) || 0}</p>
                    </CardContent>
                  </Card>

                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardContent className="p-6">
                      <p className="text-sm text-muted-foreground">Total Wallets</p>
                      <p className="text-3xl font-heading font-bold mt-2">{creditAudit.summary.total_wallets}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Wallets Table */}
                <Card className={`${cardBg} ${cardBorder}`}>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Wallet className="w-5 h-5 text-primary" />
                      Credit Wallets ({creditAudit.wallets?.length || 0})
                    </CardTitle>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => downloadCSV(
                        creditAudit.wallets || [],
                        ['entity_name', 'entity_type', 'gstin', 'email', 'balance', 'topup_balance', 'monthly_allocation', 'auto_renew', 'used_in_period', 'allocated_in_period', 'last_activity', 'last_activity_type'],
                        'credit_wallets'
                      )}
                      data-testid="export-wallets-csv"
                    >
                      <FileDown className="w-4 h-4 mr-1.5" /> Export CSV
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className={`border-b ${cardBorder}`}>
                            <th className="text-left p-3 font-medium text-muted-foreground">Entity</th>
                            <th className="text-left p-3 font-medium text-muted-foreground">Type</th>
                            <th className="text-right p-3 font-medium text-muted-foreground">Balance</th>
                            <th className="text-right p-3 font-medium text-muted-foreground">Top-up</th>
                            <th className="text-right p-3 font-medium text-muted-foreground">Monthly Alloc.</th>
                            <th className="text-right p-3 font-medium text-muted-foreground">Used (period)</th>
                            <th className="text-right p-3 font-medium text-muted-foreground">Allocated (period)</th>
                            <th className="text-left p-3 font-medium text-muted-foreground">Last Activity</th>
                            <th className="text-center p-3 font-medium text-muted-foreground">Auto Renew</th>
                          </tr>
                        </thead>
                        <tbody>
                          {creditAudit.wallets?.map((wallet, idx) => (
                            <tr key={idx} className={`border-b ${cardBorder} ${hoverBg}`}>
                              <td className="p-3">
                                <div>
                                  <p className="font-medium">{wallet.entity_name}</p>
                                  {wallet.gstin && <p className="text-xs text-muted-foreground">GSTIN: {wallet.gstin}</p>}
                                  {wallet.email && <p className="text-xs text-muted-foreground">{wallet.email}</p>}
                                </div>
                              </td>
                              <td className="p-3"><Badge variant="outline" className="text-[10px]">{wallet.entity_type}</Badge></td>
                              <td className="p-3 text-right font-heading font-semibold">{(wallet.balance || 0).toFixed(1)}</td>
                              <td className="p-3 text-right text-emerald-600">{(wallet.topup_balance || 0).toFixed(1)}</td>
                              <td className="p-3 text-right">{wallet.monthly_allocation || 0}</td>
                              <td className="p-3 text-right text-rose-600">{(wallet.used_in_period || 0).toFixed(1)}</td>
                              <td className="p-3 text-right text-emerald-600">{(wallet.allocated_in_period || 0).toFixed(1)}</td>
                              <td className="p-3 text-xs text-muted-foreground">
                                {wallet.last_activity ? (
                                  <div>
                                    <div>{wallet.last_activity.split('T')[0]}</div>
                                    <Badge variant="outline" className="text-[9px] mt-0.5">{wallet.last_activity_type}</Badge>
                                  </div>
                                ) : '—'}
                              </td>
                              <td className="p-3 text-center">
                                <Badge variant={wallet.auto_renew ? 'default' : 'secondary'} className="text-[10px]">
                                  {wallet.auto_renew ? 'Yes' : 'No'}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {(!creditAudit.wallets || creditAudit.wallets.length === 0) && (
                        <p className="text-center text-muted-foreground py-8">No wallets found</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Transactions */}
                <Card className={`${cardBg} ${cardBorder}`}>
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <History className="w-5 h-5 text-primary" />
                      Recent Credit Transactions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {creditAudit.recent_transactions?.map((trans, idx) => (
                        <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${innerCardBg}`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${trans.amount > 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                              {trans.amount > 0 ? (
                                <ArrowUpRight className="w-4 h-4 text-emerald-500" />
                              ) : (
                                <ArrowDownRight className="w-4 h-4 text-red-500" />
                              )}
                            </div>
                            <div>
                              <p className="text-sm font-medium">{trans.description?.slice(0, 50) || trans.transaction_type}</p>
                              <p className="text-xs text-muted-foreground">{trans.created_at?.split('T')[0]}</p>
                            </div>
                          </div>
                          <span className={`font-heading font-semibold ${trans.amount > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                            {trans.amount > 0 ? '+' : ''}{trans.amount?.toFixed(1)}
                          </span>
                        </div>
                      ))}
                      {(!creditAudit.recent_transactions || creditAudit.recent_transactions.length === 0) && (
                        <p className="text-center text-muted-foreground py-8">No recent transactions</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Invoices Tab */}
            {activeTab === 'invoices' && invoices && (
              <div className="space-y-8">
                {/* Invoice Summary */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardContent className="p-6">
                      <p className="text-sm text-muted-foreground">Total Invoices{invoiceFiltersActive && ' (filtered)'}</p>
                      <p className="text-3xl font-heading font-bold mt-2">{filteredInvoices.length}</p>
                      <p className="text-xs text-muted-foreground mt-2">{formatCurrency(sumTotal(filteredInvoices))}</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border-emerald-500/30">
                    <CardContent className="p-6">
                      <p className="text-sm text-emerald-600 dark:text-emerald-400">Paid</p>
                      <p className="text-3xl font-heading font-bold mt-2">{filteredPaid.length}</p>
                      <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-2">{formatCurrency(sumTotal(filteredPaid))}</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-amber-500/20 to-amber-500/5 border-amber-500/30">
                    <CardContent className="p-6">
                      <p className="text-sm text-amber-600 dark:text-amber-400">Pending</p>
                      <p className="text-3xl font-heading font-bold mt-2">{filteredPending.length}</p>
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">{formatCurrency(sumTotal(filteredPending))}</p>
                    </CardContent>
                  </Card>

                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardContent className="p-6">
                      <p className="text-sm text-muted-foreground">Collection Rate</p>
                      <p className="text-3xl font-heading font-bold mt-2">
                        {filteredInvoices.length > 0
                          ? Math.round((filteredPaid.length / filteredInvoices.length) * 100)
                          : 0}%
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Invoice List */}
                <Card className={`${cardBg} ${cardBorder}`}>
                  <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
                    <CardTitle className="font-heading flex items-center gap-2">
                      <FileText className="w-5 h-5 text-primary" />
                      Invoice List ({invoiceFiltersActive ? `${filteredInvoices.length} of ${allInvoices.length}` : allInvoices.length})
                    </CardTitle>
                    <div className="flex gap-2 items-center">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                        <Input
                          placeholder="Search invoice / customer / GSTIN..."
                          className="pl-8 h-9 w-64"
                          value={invoiceSearch}
                          onChange={(e) => setInvoiceSearch(e.target.value)}
                          data-testid="invoice-search"
                        />
                      </div>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => downloadCSV(
                          filteredInvoices,
                          ['invoice_number', 'booking_id', 'customer_name', 'customer_email', 'customer_phone', 'gstin', 'venue_name', 'room_name', 'room_type', 'description', 'booking_date', 'base_amount', 'gst_amount', 'discount_amount', 'total_amount', 'payment_method', 'razorpay_payment_id', 'status', 'created_at', 'paid_at'],
                          'invoices'
                        )}
                        data-testid="export-invoices-csv"
                      >
                        <FileDown className="w-4 h-4 mr-1.5" /> Export CSV
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Invoice filters */}
                    <div className={`flex flex-wrap items-end gap-3 mb-4 p-3 rounded-lg border ${cardBorder} ${innerCardBg}`} data-testid="invoice-filters">
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-muted-foreground">Date of Payment</span>
                        <div className="flex items-center gap-1.5">
                          <Input type="date" className="h-9 w-36" value={invoiceFilters.paidFrom}
                            max={invoiceFilters.paidTo || undefined}
                            onChange={(e) => setInvoiceFilter('paidFrom', e.target.value)} data-testid="invoice-paid-from" />
                          <span className="text-muted-foreground text-xs">to</span>
                          <Input type="date" className="h-9 w-36" value={invoiceFilters.paidTo}
                            min={invoiceFilters.paidFrom || undefined}
                            onChange={(e) => setInvoiceFilter('paidTo', e.target.value)} data-testid="invoice-paid-to" />
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-muted-foreground">Centre</span>
                        <Select value={invoiceFilters.centre} onValueChange={(v) => setInvoiceFilter('centre', v)}>
                          <SelectTrigger className="h-9 w-48" data-testid="invoice-centre-filter"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Centres</SelectItem>
                            {invoiceCentres.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-muted-foreground">Payment Type</span>
                        <Select value={invoiceFilters.paymentType} onValueChange={(v) => setInvoiceFilter('paymentType', v)}>
                          <SelectTrigger className="h-9 w-40 capitalize" data-testid="invoice-payment-type-filter"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Types</SelectItem>
                            {invoicePaymentTypes.map((t) => <SelectItem key={t} value={t} className="capitalize">{t.replace(/_/g, ' ')}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-muted-foreground">Status</span>
                        <Select value={invoiceFilters.status} onValueChange={(v) => setInvoiceFilter('status', v)}>
                          <SelectTrigger className="h-9 w-32" data-testid="invoice-status-filter"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-muted-foreground">GST</span>
                        <Select value={invoiceFilters.gst} onValueChange={(v) => setInvoiceFilter('gst', v)}>
                          <SelectTrigger className="h-9 w-44" data-testid="invoice-gst-filter"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="with">With GSTIN (B2B)</SelectItem>
                            <SelectItem value="without">Without GSTIN (B2C)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {invoiceFiltersActive && (
                        <Button size="sm" variant="ghost" className="h-9"
                          onClick={() => { setInvoiceFilters(EMPTY_INVOICE_FILTERS); setInvoiceSearch(''); }}
                          data-testid="invoice-clear-filters">
                          Clear filters
                        </Button>
                      )}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className={`border-b ${cardBorder}`}>
                            <th className="text-left p-3 font-medium text-muted-foreground">Invoice #</th>
                            <th className="text-left p-3 font-medium text-muted-foreground">Customer</th>
                            <th className="text-left p-3 font-medium text-muted-foreground">GSTIN</th>
                            <th className="text-left p-3 font-medium text-muted-foreground">Centre / Room</th>
                            <th className="text-left p-3 font-medium text-muted-foreground">Booking Date</th>
                            <th className="text-right p-3 font-medium text-muted-foreground">Base</th>
                            <th className="text-right p-3 font-medium text-muted-foreground">GST</th>
                            <th className="text-right p-3 font-medium text-muted-foreground">Discount</th>
                            <th className="text-right p-3 font-medium text-muted-foreground">Total</th>
                            <th className="text-left p-3 font-medium text-muted-foreground">Payment</th>
                            <th className="text-center p-3 font-medium text-muted-foreground">Status</th>
                            <th className="text-left p-3 font-medium text-muted-foreground">Date of Payment</th>
                            <th className="text-left p-3 font-medium text-muted-foreground">Issued</th>
                            <th className="text-center p-3 font-medium text-muted-foreground">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredInvoices.map((inv, idx) => (
                            <tr key={idx} className={`border-b ${cardBorder} ${hoverBg}`}>
                              <td className="p-3 font-mono text-xs">{inv.invoice_number}</td>
                              <td className="p-3">
                                <div className="font-medium">{inv.customer_name}</div>
                                {inv.customer_email && <div className="text-xs text-muted-foreground">{inv.customer_email}</div>}
                              </td>
                              <td className="p-3 text-xs text-muted-foreground">{inv.gstin || '—'}</td>
                              <td className="p-3 text-xs">
                                <div>{inv.venue_name}</div>
                                <div className="text-muted-foreground">{inv.room_name}</div>
                                {inv.description && <div className="font-medium max-w-[220px]" title={inv.description}>{inv.description}</div>}
                              </td>
                              <td className="p-3 text-xs">{inv.booking_date || '—'}</td>
                              <td className="p-3 text-right">{fmtCurrency(inv.base_amount)}</td>
                              <td className="p-3 text-right text-muted-foreground">{fmtCurrency(inv.gst_amount)}</td>
                              <td className="p-3 text-right text-rose-600">{inv.discount_amount > 0 ? `-${fmtCurrency(inv.discount_amount)}` : '—'}</td>
                              <td className="p-3 text-right font-heading font-semibold">{fmtCurrency(inv.total_amount)}</td>
                              <td className="p-3"><Badge variant="outline" className="text-[10px]">{(inv.payment_method || '—').replace('_', ' ')}</Badge></td>
                              <td className="p-3 text-center">
                                <Badge className={inv.status === 'paid' ? 'bg-emerald-500/20 text-emerald-700 text-[10px]' : 'bg-amber-500/20 text-amber-700 text-[10px]'}>
                                  {inv.status}
                                </Badge>
                              </td>
                              <td className="p-3 text-xs whitespace-nowrap">{toLocalDate(inv.paid_at) || '—'}</td>
                              <td className="p-3 text-xs text-muted-foreground">{inv.created_at?.split('T')[0] || '—'}</td>
                              <td className="p-3 text-center">
                                {inv.invoice_url ? (
                                  <a href={inv.invoice_url} target="_blank" rel="noreferrer">
                                    <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`dl-invoice-${inv.invoice_id}`}>
                                      <FileDown className="w-3 h-3 mr-1" /> View
                                    </Button>
                                  </a>
                                ) : <span className="text-xs text-muted-foreground">—</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {allInvoices.length === 0 && (
                        <p className="text-center text-muted-foreground py-8">No invoices found in the selected range</p>
                      )}
                      {allInvoices.length > 0 && filteredInvoices.length === 0 && (
                        <p className="text-center text-muted-foreground py-8">No invoices match the selected filters</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Transactions Tab */}
            {activeTab === 'transactions' && transactions && (
              <div className="space-y-8">
                {/* Transaction Summary */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardContent className="p-6">
                      <p className="text-sm text-muted-foreground">Total Transactions</p>
                      <p className="text-3xl font-heading font-bold mt-2">{transactions.summary.total_transactions}</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border-emerald-500/30">
                    <CardContent className="p-6">
                      <p className="text-sm text-emerald-600 dark:text-emerald-400">Credits Allocated</p>
                      <p className="text-3xl font-heading font-bold mt-2">{transactions.summary.credits_allocated?.toFixed(1) || 0}</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-red-500/20 to-red-500/5 border-red-500/30">
                    <CardContent className="p-6">
                      <p className="text-sm text-red-600 dark:text-red-400">Credits Used</p>
                      <p className="text-3xl font-heading font-bold mt-2">{transactions.summary.credits_used?.toFixed(1) || 0}</p>
                    </CardContent>
                  </Card>

                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardContent className="p-6">
                      <p className="text-sm text-muted-foreground">Net Flow</p>
                      <p className={`text-3xl font-heading font-bold mt-2 ${transactions.summary.net_flow >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {transactions.summary.net_flow >= 0 ? '+' : ''}{transactions.summary.net_flow?.toFixed(1) || 0}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* by-type breakdown */}
                {transactions.by_type && Object.keys(transactions.by_type).length > 0 && (
                  <Card className={`${cardBg} ${cardBorder}`}>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Breakdown by Transaction Type</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                      {Object.entries(transactions.by_type).map(([t, v]) => (
                        <div key={t} className={`p-3 rounded-md border ${cardBorder}`}>
                          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t.replace(/_/g, ' ')}</p>
                          <p className="text-sm font-bold">{v.count} · {Number(v.credits || 0).toFixed(1)} cr</p>
                          {v.inr > 0 && <p className="text-xs text-muted-foreground">{fmtCurrency(v.inr)}</p>}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Transaction Table */}
                <Card className={`${cardBg} ${cardBorder}`}>
                  <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
                    <CardTitle className="font-heading flex items-center gap-2">
                      <History className="w-5 h-5 text-primary" />
                      All Transactions ({transactions.transactions?.length || 0})
                    </CardTitle>
                    <div className="flex gap-2 items-center">
                      <Select value={txnTypeFilter} onValueChange={setTxnTypeFilter}>
                        <SelectTrigger className="h-9 w-44" data-testid="txn-type-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          <SelectItem value="purchase">Purchase</SelectItem>
                          <SelectItem value="allocation">Allocation</SelectItem>
                          <SelectItem value="auto_renewal">Auto Renewal</SelectItem>
                          <SelectItem value="debit">Debit (Booking)</SelectItem>
                          <SelectItem value="refund">Refund</SelectItem>
                          <SelectItem value="adjustment">Adjustment</SelectItem>
                          <SelectItem value="vo_initial_allocation">VO Initial</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => downloadCSV(
                          transactions.transactions || [],
                          ['id', 'created_at', 'transaction_type', 'entity_name', 'entity_type', 'entity_email', 'amount', 'payment_amount', 'description', 'booking_id', 'booking_venue', 'booking_room', 'booking_date', 'booking_amount', 'wallet_id'],
                          'transactions'
                        )}
                        data-testid="export-txns-csv"
                      >
                        <FileDown className="w-4 h-4 mr-1.5" /> Export CSV
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className={`border-b ${cardBorder}`}>
                            <th className="text-left p-3 font-medium text-muted-foreground">Date</th>
                            <th className="text-left p-3 font-medium text-muted-foreground">Type</th>
                            <th className="text-left p-3 font-medium text-muted-foreground">Entity</th>
                            <th className="text-right p-3 font-medium text-muted-foreground">Credits</th>
                            <th className="text-right p-3 font-medium text-muted-foreground">INR Paid</th>
                            <th className="text-left p-3 font-medium text-muted-foreground">Booking / Description</th>
                            <th className="text-left p-3 font-medium text-muted-foreground">Booking ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.transactions?.map((t, idx) => (
                            <tr key={idx} className={`border-b ${cardBorder} ${hoverBg}`}>
                              <td className="p-3 text-xs whitespace-nowrap">{t.created_at?.split('T')[0] || '—'}</td>
                              <td className="p-3"><Badge variant="outline" className="text-[10px]">{(t.transaction_type || '').replace(/_/g, ' ')}</Badge></td>
                              <td className="p-3">
                                <div className="font-medium text-sm">{t.entity_name}</div>
                                {t.entity_email && <div className="text-xs text-muted-foreground">{t.entity_email}</div>}
                              </td>
                              <td className={`p-3 text-right font-heading font-semibold ${t.amount > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {t.amount > 0 ? '+' : ''}{Number(t.amount || 0).toFixed(1)}
                              </td>
                              <td className="p-3 text-right text-muted-foreground">{t.payment_amount > 0 ? fmtCurrency(t.payment_amount) : '—'}</td>
                              <td className="p-3">
                                {t.booking_venue ? (
                                  <div className="text-xs">
                                    <div className="font-medium">{t.booking_room} @ {t.booking_venue}</div>
                                    <div className="text-muted-foreground">{t.booking_date} · {fmtCurrency(t.booking_amount)}</div>
                                  </div>
                                ) : (
                                  <span className="text-xs text-muted-foreground">{t.description?.slice(0, 80) || '—'}</span>
                                )}
                              </td>
                              <td className="p-3 font-mono text-[10px] text-muted-foreground">
                                {t.booking_id ? `#${t.booking_id.slice(0, 8)}` : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {(!transactions.transactions || transactions.transactions.length === 0) && (
                        <p className="text-center text-muted-foreground py-8">No transactions in the selected range</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default FinanceDashboard;
